// src/Pages/wishes.js
import React, { useState, useEffect } from 'react';
import '../Styles/wishes.css';
import { Link } from 'react-router-dom';
import SERVER from '../server.js'; 

const unactiveHomeIcon = `${SERVER}/img/home-unactive.png`;
const activeWishlistIcon = `${SERVER}/img/wishlist-active.png`;
const unactiveCartIcon = `${SERVER}/img/cart-unactive.png`;
const unactiveFriendsIcon = `${SERVER}/img/friends-unactive.png`;
const errorimg = `${SERVER}/img/error.png`;
const activeCartIcon = `${SERVER}/img/cart-active.png`;

// Notification Component
const Notification = ({ message, type, visible, onClose }) => {
  useEffect(() => {
    if (visible) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div className={`notification ${type}`}>
      <span>{message}</span>
      <button onClick={onClose} className="notification-close">×</button>
    </div>
  );
};

function Wishes() {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState({ message: '', type: '', visible: false });
  const [isUpdating, setIsUpdating] = useState({});
  const [cartItemCount, setCartItemCount] = useState(0);

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type, visible: true });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, visible: false }));
  };

  useEffect(() => {
    const initUser = async () => {
      if (window.Telegram && window.Telegram.WebApp) {
        const tg = window.Telegram.WebApp;
        const userData = tg.initDataUnsafe?.user;
        
        if (userData) {
          const userInfo = {
            id: userData.id,
            firstName: userData.first_name,
            lastName: userData.last_name,
            username: userData.username,
            languageCode: userData.language_code,
            isPremium: userData.is_premium
          };
          setUser(userInfo);
          await fetchUserData(userInfo.id);
        } else {
          setError('Telegram user data not available. Please open this app through Telegram.');
          setLoading(false);
        }
      } else {
        setError('This app can only be used through Telegram. Please open it via Telegram Web App.');
        setLoading(false);
      }
    };

    initUser();
  }, []);

  const fetchUserData = async (userId) => {
    try {
      // Fetch wishlist items
      const wishlistResponse = await fetch(`${SERVER}/users/${userId}/wishlist`);
      if (wishlistResponse.ok) {
        const wishlistData = await wishlistResponse.json();
        setWishlistItems(wishlistData || []);
      }

      // Fetch cart count
      const cartResponse = await fetch(`${SERVER}/users/${userId}/cart`);
      if (cartResponse.ok) {
        const cartData = await cartResponse.json();
        setCartItemCount(cartData.items ? cartData.items.length : 0);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    if (!user) {
      showNotification('Please log in to manage your wishlist', 'error');
      return;
    }
    
    setIsUpdating(prev => ({ ...prev, [productId]: true }));
    
    try {
      const response = await fetch(
        `${SERVER}/users/${user.id}/wishlist/${productId}`,
        { method: 'DELETE' }
      );
      
      if (!response.ok) {
        throw new Error('Failed to remove from wishlist');
      }
      
      setWishlistItems(prevItems => 
        prevItems.filter(item => item.product_id !== productId)
      );
      showNotification('Item removed from wishlist', 'success');
    } catch (err) {
      setError(err.message);
      showNotification('Failed to remove item from wishlist', 'error');
    } finally {
      setIsUpdating(prev => ({ ...prev, [productId]: false }));
    }
  };

  const toggleCart = async (productId) => {
    if (!user) {
      showNotification('Please log in to manage your cart', 'error');
      return;
    }
    
    setIsUpdating(prev => ({ ...prev, [productId]: true }));
    
    try {
      const item = wishlistItems.find(item => item.product_id === productId);
      if (!item) return;
      
      if (item.inCart) {
        // Remove from cart
        const response = await fetch(
          `${SERVER}/users/${user.id}/cart/${productId}`,
          { method: 'DELETE' }
        );
        
        if (!response.ok) {
          throw new Error('Failed to remove from cart');
        }
        
        showNotification('Item removed from cart', 'success');
      } else {
        // Add to cart
        const response = await fetch(
          `${SERVER}/users/${user.id}/cart`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              product_id: productId,
              quantity: 1
            })
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }
        
        showNotification('Item added to cart! 🛒', 'success');
      }
      
      // Update local state
      setWishlistItems(prevItems =>
        prevItems.map(item =>
          item.product_id === productId
            ? { ...item, inCart: !item.inCart }
            : item
        )
      );
    } catch (err) {
      setError(err.message);
      showNotification('Failed to update cart', 'error');
    } finally {
      setIsUpdating(prev => ({ ...prev, [productId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="wishes-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <div>Loading your wishlist...</div>
          <small>Gathering your favorite items</small>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wishes-container">
        <div className="error">
          <div className="error-icon">❤️</div>
          <strong>Unable to load your wishlist</strong>
          <br />
          {error}
          <br />
          <div className="error-actions">
            <button onClick={() => window.location.reload()} className="retry-btn">
              Try Again
            </button>
            <Link to="/" className="browse-products-btn">
              Browse Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wishes-container">
      <Notification
        message={notification.message}
        type={notification.type}
        visible={notification.visible}
        onClose={closeNotification}
      />

      <header className="wishes-header">
        <div className="header-content">
          <div className="heart">❤️</div>
          <div className="header-text">
            <h2>YOUR WISHLIST</h2>
            <p className="wishes-subtitle">
              {wishlistItems.length > 0 
                ? `${wishlistItems.length} item${wishlistItems.length !== 1 ? 's' : ''} saved for later`
                : 'Save items you love for later'
              }
            </p>
          </div>
        </div>
      </header>

      <div className="wishlist-items">
        {wishlistItems.length === 0 ? (
          <div className="empty-wishlist">
            <div className="empty-wishlist-icon">💝</div>
            <h2>Your wishlist is empty</h2>
            <p>Start adding items you love to keep track of them easily!</p>
            <div className="empty-wishlist-actions">
              <Link to="/" className="btn-shop">
                Discover Products
              </Link>
              <Link to="/Cart" className="btn-cart">
                View Cart
              </Link>
            </div>
            <div className="wishlist-benefits">
              <div className="benefit">
                <span className="benefit-icon">💝</span>
                <span>Save items for later</span>
              </div>
              <div className="benefit">
                <span className="benefit-icon">🔔</span>
                <span>Get notified of price drops</span>
              </div>
              <div className="benefit">
                <span className="benefit-icon">🎁</span>
                <span>Perfect for gift planning</span>
              </div>
            </div>
          </div>
        ) : (
          wishlistItems.map(item => (
            <div className="wish-item-container" key={item.product_id}>
              <div className="wish-item-actions">
                <button 
                  className={`cart-btn ${item.inCart ? 'active' : ''}`}
                  onClick={() => toggleCart(item.product_id)}
                  disabled={isUpdating[item.product_id]}
                  title={item.inCart ? 'Remove from cart' : 'Add to cart'}
                >
                  {isUpdating[item.product_id] ? (
                    <span className="loading-dot">⋯</span>
                  ) : (
                    <img 
                      src={item.inCart ? activeCartIcon : unactiveCartIcon} 
                      alt="Cart" 
                      className="action-icon" 
                    />
                  )}
                </button>
                <button 
                  className="wishlist-btn"
                  onClick={() => removeFromWishlist(item.product_id)}
                  disabled={isUpdating[item.product_id]}
                  title="Remove from wishlist"
                >
                  {isUpdating[item.product_id] ? (
                    <span className="loading-dot">⋯</span>
                  ) : (
                    <img 
                      src={activeWishlistIcon} 
                      alt="Remove from wishlist" 
                      className="action-icon" 
                    />
                  )}
                </button>
              </div>
              
              <div className="wish-item">
                <div className="item-image">
                  <img 
                    src={item.photo_path ? `${SERVER}/img/${item.photo_path}` : errorimg} 
                    alt={item.name} 
                    className="product-image" 
                    onError={(e) => {
                      e.target.src = errorimg;
                    }}
                  />
                  {item.inCart && (
                    <div className="in-cart-badge"></div>
                  )}
                </div>
                <div className="item-content">
                  <div className="name_price">
                    <h3 className="item-title">{item.name}</h3>
                    <h3 className="item-price">${item.price.toFixed(2)} / 1 UNIT</h3>
                  </div>
                  <p className="item-description">{item.description}</p>
                  <div className="item-footer">

                    <div className="item-rating">
                      <span className="stars">⭐⭐⭐⭐⭐</span>
                      <span className="rating-text">(5.0)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <nav className="bottom-nav">
        <Link to="/" className="nav-btn">
          <img src={unactiveHomeIcon} alt="Home" className="nav-icon" />
          <span>Home</span>
        </Link>
        <Link to="/Wishlist" className="nav-btn active">
          <img src={activeWishlistIcon} alt="Wishlist" className="nav-icon" />
          <span>Wishes</span>
          {wishlistItems.length > 0 && (
            <span className="nav-badge">{wishlistItems.length}</span>
          )}
        </Link>
        <Link to="/Cart" className="nav-btn">
          <img src={unactiveCartIcon} alt="Cart" className="nav-icon" />
          <span>Cart</span>
          {cartItemCount > 0 && (
            <span className="nav-badge">{cartItemCount}</span>
          )}
        </Link>
        <Link to="/Friends" className="nav-btn">
          <img src={unactiveFriendsIcon} alt="Friends" className="nav-icon" />
          <span>Friends</span>
        </Link>
      </nav>
    </div>
  );
}

export default Wishes;